import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Mail, Lock, Loader2, RefreshCw, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import PasswordInput from "@/components/PasswordInput";
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable, authenticateWithBiometric, checkBiometricAvailable } from "@/utils/webauthn";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Check if biometrics are supported on this device
  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setBiometricAvailable);
  }, []);

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }
    
    setResendingVerification(true);
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      toast.success(data.message || "Verification email sent! Please check your inbox.");
      setShowResendVerification(false);
    } catch (error) {
      toast.error("Failed to send verification email. Please try again.");
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResendVerification(false);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/auth/login`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.withCredentials = true;
    xhr.timeout = 30000; // 30 second timeout
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          // Pass session_token to login for iOS Safari localStorage fallback
          login(data, data.session_token);
          toast.success("Welcome back!");
          navigate("/dashboard");
        } else {
          // Check if error is about email verification
          const errorMessage = data.detail || "Login failed";
          if (errorMessage.toLowerCase().includes("verify") || errorMessage.toLowerCase().includes("verification")) {
            setShowResendVerification(true);
          }
          toast.error(errorMessage);
        }
      } catch (e) {
        toast.error("Server error. Please try again.");
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error("Connection error. Please check your internet and try again.");
    };

    xhr.ontimeout = function() {
      setLoading(false);
      toast.error("Request timed out. Please try again.");
    };
    
    xhr.send(JSON.stringify({ email, password }));
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const data = await authenticateWithBiometric(email);
      login(data, data.session_token);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Biometric authentication was cancelled");
      } else {
        toast.error(err.message || "Biometric login failed");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 pb-24 md:pb-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 md:mb-8">
          <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-10 md:h-12" />
        </Link>

        <Card className="border-border bg-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl uppercase">Welcome Back</CardTitle>
            <CardDescription>Sign in to access your tool inventory</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Google Login */}
            <Button
              variant="outline"
              className="w-full border-secondary hover:border-primary"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {biometricAvailable && (
              <Button
                variant="outline"
                className="w-full border-secondary hover:border-primary"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                data-testid="biometric-login-btn"
              >
                {biometricLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Fingerprint className="w-5 h-5 mr-2" />
                )}
                Login with Biometrics
              </Button>
            )}

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                OR
              </span>
            </div>

            {/* Email Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-background border-input"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-background border-input"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>
            </form>

            {/* Resend Verification Email */}
            {showResendVerification && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-200 mb-2">
                  Your email hasn't been verified yet.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  data-testid="resend-verification-btn"
                >
                  {resendingVerification ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Resend Verification Email</>
                  )}
                </Button>
              </div>
            )}

            {/* Always visible link to resend verification */}
            <div className="text-center">
              <button 
                type="button"
                onClick={() => setShowResendVerification(true)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Didn't receive verification email?
              </button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
